import React, { useState, useEffect, useCallback } from "react";
import DashboardHeader from "../../layouts/dashboard_header";
import SideMenu from "../../layouts/dashboard_menu";
import DashboardFooter from "../../layouts/dashboard_footer";
import Canvas from "../../layouts/dahboard_canvas";
import { buildApiUrl, useBaseUrl } from "../../../utils/utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import localForage from "localforage";
import Swal from "sweetalert2";

/* ─── Types ─── */
interface ClinicPatient {
    patient_id: string;
    id: string;
    first_name: string;
    last_name: string;
    middle_name?: string;
    email: string;
    phone?: string;
    date_enrolled?: string;
}

interface ServiceRequest {
    id: number;
    title: string;
    description: string;
    status: string;
    priority: string;
    engagement_type: string;
    request_type: 'open' | 'direct';
    patient_name?: string;
    clinic_name?: string;
    created_at: string;
    updated_at: string;
    start_date?: string;
    end_date?: string;
    response_time?: number;
    min_pay?: number;
    max_pay?: number;
    required_services?: string[];
    patients?: { patient_id: string; name: string }[];
    creator: { id: number; name: string };
    direct_partners?: number[];
}

interface Application {
    id: number;
    service_request: {
        id: number;
        title: string;
        status: string;
        priority: string;
        description?: string;
        creator_name?: string;
    };
    applicant?: {
        id: number;
        name: string;
    };
    partner_applying_name?: string;
    status: string;
    created: string;
    partner_minimum?: number;
    partner_maximum?: number;
    partner_qualifications?: string;
    partner_certifications?: string[];
    partner_equipment?: string[];
    application_notes?: string;
}

interface PaginationMeta {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    hasNext: boolean;
    hasPrevious: boolean;
}

interface Stats {
    totalRequests: number;
    myRequests: number;
    openRequests: number;
    directRequests: number;
    receivedApps: number;
    sentApps: number;
    negotiations: number;
}

type Tab = 'overview' | 'my-requests' | 'browse' | 'direct' | 'applications';
type ViewMode = 'grid' | 'list';

interface ApplyFormState {
    partner_minimum: string;
    partner_maximum: string;
    pay_mode: string;
    patient_flow: string;
    patient_flow_rate: string;
    partner_qualifications: string;
    partner_experience_years: string;
    partner_certifications: string[];
    partner_equipment: string[];
    application_notes: string;
}

/* ─── Helpers ─── */
const fmtDate = (d?: string) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
};

const fmtPay = (min?: number, max?: number) => {
    if (!min && !max) return '—';
    if (min && max) return `KES ${min.toLocaleString()} – ${max.toLocaleString()}`;
    return `KES ${(min || max || 0).toLocaleString()}`;
};

const emptyPagination = (): PaginationMeta => ({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
});

const createEmptyApplyForm = (): ApplyFormState => ({
    partner_minimum: '',
    partner_maximum: '',
    pay_mode: 'milestone',
    patient_flow: '5',
    patient_flow_rate: 'daily',
    partner_qualifications: '',
    partner_experience_years: '',
    partner_certifications: [],
    partner_equipment: [],
    application_notes: ''
});

/* ─── Component ─── */
const ServiceCenter: React.FC = () => {
    const navigate = useNavigate();
    const baseUrl = useBaseUrl();
    const [searchParams, setSearchParams] = useSearchParams();

    const [clinicId, setClinicId] = useState<number | null>(null);
    const [clinicName, setClinicName] = useState('');
    const [tab, setTab] = useState<Tab>('overview');
    const [loading, setLoading] = useState(true);

    const requestPageSize = 12;
    const applicationPageSize = 12;

    const [myRequests, setMyRequests] = useState<ServiceRequest[]>([]);
    const [openRequests, setOpenRequests] = useState<ServiceRequest[]>([]);
    const [directRequests, setDirectRequests] = useState<ServiceRequest[]>([]);
    const [receivedApps, setReceivedApps] = useState<Application[]>([]);
    const [sentApps, setSentApps] = useState<Application[]>([]);
    const [myRequestsPagination, setMyRequestsPagination] = useState<PaginationMeta>(emptyPagination());
    const [openRequestsPagination, setOpenRequestsPagination] = useState<PaginationMeta>(emptyPagination());
    const [directRequestsPagination, setDirectRequestsPagination] = useState<PaginationMeta>(emptyPagination());
    const [receivedAppsPagination, setReceivedAppsPagination] = useState<PaginationMeta>(emptyPagination());
    const [sentAppsPagination, setSentAppsPagination] = useState<PaginationMeta>(emptyPagination());
    const [stats, setStats] = useState<Stats>({ totalRequests: 0, myRequests: 0, openRequests: 0, directRequests: 0, receivedApps: 0, sentApps: 0, negotiations: 0 });

    const [requestSearch, setRequestSearch] = useState('');
    const [requestStatusFilter, setRequestStatusFilter] = useState('');
    const [requestPriorityFilter, setRequestPriorityFilter] = useState('');
    const [requestDateFrom, setRequestDateFrom] = useState('');
    const [requestDateTo, setRequestDateTo] = useState('');
    const [applicationSearch, setApplicationSearch] = useState('');
    const [applicationStatusFilter, setApplicationStatusFilter] = useState('');
    const [applicationDateFrom, setApplicationDateFrom] = useState('');
    const [applicationDateTo, setApplicationDateTo] = useState('');

    const [showCreate, setShowCreate] = useState(false);
    const [detailRequest, setDetailRequest] = useState<ServiceRequest | null>(null);
    const [showApply, setShowApply] = useState<ServiceRequest | null>(null);
    const [appTab, setAppTab] = useState<'received' | 'sent'>('received');
    const [requestViewMode, setRequestViewMode] = useState<ViewMode>('grid');
    const [applicationViewMode, setApplicationViewMode] = useState<ViewMode>('grid');

    const [createStep, setCreateStep] = useState(1);
    const [applyStep, setApplyStep] = useState(1);
    const [createForm, setCreateForm] = useState({
        title: '', description: '', request_type: 'open' as 'open' | 'direct',
        priority: 'normal', patient_ids: [] as string[], engagement_type: 'consultation',
        start_date: '', end_date: '', response_time: 48,
        pay_mode: 'milestone', min_pay: '', max_pay: '',
        share_patient_demographics: true, share_medical_history: true,
        share_current_medications: true, share_lab_results: true,
        share_imaging: true, share_notes: true,
        required_services: [] as number[], direct_partners: [] as number[],
        contract_duration: 'medium_term', contract_term: 'term', renewable: false,
        patient_consent_notes: ''
    });

    const [applyForm, setApplyForm] = useState<ApplyFormState>(createEmptyApplyForm());
    const [applyCertificationDraft, setApplyCertificationDraft] = useState('');
    const [applyEquipmentDraft, setApplyEquipmentDraft] = useState('');

    const [availableServices, setAvailableServices] = useState<any[]>([]);
    const [servicesSearch, setServicesSearch] = useState('');
    const [servicesPage, setServicesPage] = useState(1);
    const [servicesPagination, setServicesPagination] = useState<PaginationMeta>({ currentPage: 1, totalPages: 1, totalItems: 0, hasNext: false, hasPrevious: false });
    const [servicesLoading, setServicesLoading] = useState(false);
    const [clinicServiceIds, setClinicServiceIds] = useState<number[]>([]);
    const [clinicServicesLoaded, setClinicServicesLoaded] = useState(false);
    const [availablePartners, setAvailablePartners] = useState<any[]>([]);
    const [partnerEmailSearch, setPartnerEmailSearch] = useState('');

    const [clinicPatients, setClinicPatients] = useState<ClinicPatient[]>([]);
    const [patientSearch, setPatientSearch] = useState('');
    const [patientsLoading, setPatientsLoading] = useState(false);

    const [applyAttachments, setApplyAttachments] = useState<{ label: string; file: File | null }[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [viewingApplication, setViewingApplication] = useState<any | null>(null);
    const [viewingAppLoading, setViewingAppLoading] = useState(false);
    const [amendMode, setAmendMode] = useState(false);
    const [amendForm, setAmendForm] = useState({ partner_minimum: '', partner_maximum: '', pay_mode: '', patient_flow: '', patient_flow_rate: '', partner_qualifications: '', application_notes: '' });
    const [amendSubmitting, setAmendSubmitting] = useState(false);
    const [negotiationMsg, setNegotiationMsg] = useState('');

    /* ─── Init ─── */
    useEffect(() => {
        document.title = "Service Request Center - Uzima Healthcare";
        (async () => {
            const cId = await localForage.getItem("clinic_id") as number | null;
            const cName = await localForage.getItem("clinic_name") as string || '';
            if (cId) { setClinicId(cId); setClinicName(cName); }
        })();
    }, []);

    /* ─── Deep link: /institution/service-center?tab=applications&appTab=received&appId=123 ─── */
    useEffect(() => {
        const deepLinkAppId = searchParams.get('appId');
        const deepLinkTab = searchParams.get('tab');
        const deepLinkAppTab = searchParams.get('appTab');
        if (deepLinkTab === 'applications') setTab('applications');
        if (deepLinkAppTab === 'received' || deepLinkAppTab === 'sent') setAppTab(deepLinkAppTab);
        if (deepLinkAppId) {
            openApplicationDetail(Number(deepLinkAppId));
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.delete('appId');
                return next;
            }, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const readPagination = (pagination: any, fallbackLength: number = 0): PaginationMeta => ({
        currentPage: Number(pagination?.current_page ?? pagination?.currentPage ?? 1),
        totalPages: Number(pagination?.total_pages ?? pagination?.totalPages ?? 1),
        totalItems: Number(pagination?.total_items ?? pagination?.totalItems ?? fallbackLength),
        hasNext: Boolean(pagination?.has_next ?? pagination?.hasNext ?? false),
        hasPrevious: Boolean(pagination?.has_previous ?? pagination?.hasPrevious ?? false)
    });

    const addApplyArrayItem = (field: 'partner_certifications' | 'partner_equipment', draftValue: string, clearDraft: () => void) => {
        const normalizedValue = draftValue.trim();
        if (!normalizedValue) return;

        setApplyForm(prev => ({
            ...prev,
            [field]: prev[field].includes(normalizedValue) ? prev[field] : [...prev[field], normalizedValue]
        }));
        clearDraft();
    };

    const removeApplyArrayItem = (field: 'partner_certifications' | 'partner_equipment', value: string) => {
        setApplyForm(prev => ({
            ...prev,
            [field]: prev[field].filter(item => item !== value)
        }));
    };

    const normalizeApplication = (application: any): Application => ({
        id: application.id,
        service_request: {
            id: application.service_request?.id,
            title: application.service_request?.title,
            status: application.service_request?.status,
            priority: application.service_request?.priority,
            description: application.service_request?.description,
            creator_name: application.service_request?.creator?.name || application.service_request?.creator_name
        },
        applicant: application.applicant,
        partner_applying_name: application.applicant?.name || application.partner_applying_name,
        status: application.status,
        created: application.application_date || application.created,
        partner_minimum: application.proposed_rate || application.partner_minimum,
        partner_maximum: application.partner_maximum,
        partner_qualifications: Array.isArray(application.qualifications)
            ? application.qualifications.join(', ')
            : application.partner_qualifications,
        partner_certifications: Array.isArray(application.partner_certifications)
            ? application.partner_certifications
            : [],
        partner_equipment: Array.isArray(application.partner_equipment)
            ? application.partner_equipment
            : [],
        application_notes: application.application_notes
    });

    const refreshCountsAndPreviews = useCallback(async () => {
        if (!clinicId) return;

        setLoading(true);
        try {
            const [mineRes, publicRes, directRes, receivedRes, sentRes] = await Promise.all([
                fetch(buildApiUrl(baseUrl, `collaborations/service-requests/?creator_id=${clinicId}&page=1&page_size=4&sort_by=created&sort_direction=desc`)),
                fetch(buildApiUrl(baseUrl, `collaborations/general-requests/?clinic_id=${clinicId}&exclude_creator=true&exclude_applied=true&page=1&page_size=4&sort_by=created&sort_direction=desc`)),
                fetch(buildApiUrl(baseUrl, `collaborations/private-requests/?clinic_id=${clinicId}&exclude_applied=true&page=1&page_size=4&sort_by=created&sort_direction=desc`)),
                fetch(buildApiUrl(baseUrl, `collaborations/applications/received/?clinic_id=${clinicId}&page=1&page_size=5`)),
                fetch(buildApiUrl(baseUrl, `collaborations/applications/sent/?clinic_id=${clinicId}&page=1&page_size=5`))
            ]);

            const mineData = mineRes.ok ? await mineRes.json() : null;
            const publicData = publicRes.ok ? await publicRes.json() : null;
            const directData = directRes.ok ? await directRes.json() : null;
            const receivedData = receivedRes.ok ? await receivedRes.json() : null;
            const sentData = sentRes.ok ? await sentRes.json() : null;

            const mineRequests = mineData?.data?.service_requests || [];
            const publicRequests = publicData?.data?.requests || [];
            const directRequestList = directData?.data?.requests || [];
            const normalizedReceived = (receivedData?.data?.applications || []).map(normalizeApplication);
            const normalizedSent = (sentData?.data?.applications || []).map(normalizeApplication);
            const minePagination = readPagination(mineData?.data?.pagination || mineData?.data, mineRequests.length);
            const publicPagination = readPagination(publicData?.data?.pagination, publicRequests.length);
            const directPagination = readPagination(directData?.data?.pagination, directRequestList.length);
            const receivedPagination = readPagination(receivedData?.data?.pagination, normalizedReceived.length);
            const sentPagination = readPagination(sentData?.data?.pagination, normalizedSent.length);

            setMyRequests(mineRequests);
            setOpenRequests(publicRequests);
            setDirectRequests(directRequestList);
            setReceivedApps(normalizedReceived);
            setSentApps(normalizedSent);
            setMyRequestsPagination(minePagination);
            setOpenRequestsPagination(publicPagination);
            setDirectRequestsPagination(directPagination);
            setReceivedAppsPagination(receivedPagination);
            setSentAppsPagination(sentPagination);
            setStats({
                totalRequests: minePagination.totalItems + publicPagination.totalItems + directPagination.totalItems,
                myRequests: minePagination.totalItems,
                openRequests: publicPagination.totalItems,
                directRequests: directPagination.totalItems,
                receivedApps: receivedPagination.totalItems,
                sentApps: sentPagination.totalItems,
                negotiations: normalizedReceived.filter((application: Application) => application.status === 'negotiating').length
            });
        } catch (err) {
            console.error('Service center fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, [applicationDateFrom, applicationDateTo, applicationPageSize, applicationSearch, applicationStatusFilter, baseUrl, clinicId, receivedAppsPagination.currentPage, sentAppsPagination.currentPage]);

    const fetchMyRequests = useCallback(async (page: number) => {
        if (!clinicId) return;

        setLoading(true);
        try {
            const params = new URLSearchParams({
                creator_id: clinicId.toString(),
                page: page.toString(),
                page_size: requestPageSize.toString(),
                search: requestSearch,
                status: requestStatusFilter,
                priority: requestPriorityFilter,
                date_from: requestDateFrom,
                date_to: requestDateTo,
                sort_by: 'created',
                sort_direction: 'desc'
            });
            const response = await fetch(buildApiUrl(baseUrl, `collaborations/service-requests/?${params.toString()}`));
            const data = response.ok ? await response.json() : null;
            const list = data?.data?.service_requests || [];
            const pagination = readPagination(data?.data?.pagination || data?.data, list.length);

            setMyRequests(list);
            setMyRequestsPagination(pagination);
            setStats(prev => ({ ...prev, myRequests: pagination.totalItems, totalRequests: pagination.totalItems + prev.openRequests + prev.directRequests }));
        } catch (err) {
            console.error('My requests fetch error:', err);
            setMyRequests([]);
            setMyRequestsPagination(emptyPagination());
        } finally {
            setLoading(false);
        }
    }, [baseUrl, clinicId, requestDateFrom, requestDateTo, requestPageSize, requestPriorityFilter, requestSearch, requestStatusFilter]);

    const fetchOpenRequests = useCallback(async (page: number) => {
        if (!clinicId) return;

        setLoading(true);
        try {
            const params = new URLSearchParams({
                clinic_id: clinicId.toString(),
                exclude_creator: 'true',
                exclude_applied: 'true',
                page: page.toString(),
                page_size: requestPageSize.toString(),
                search: requestSearch,
                status: requestStatusFilter,
                priority: requestPriorityFilter,
                date_from: requestDateFrom,
                date_to: requestDateTo,
                sort_by: 'created',
                sort_direction: 'desc'
            });
            const response = await fetch(buildApiUrl(baseUrl, `collaborations/general-requests/?${params.toString()}`));
            const data = response.ok ? await response.json() : null;
            const list = data?.data?.requests || [];
            const pagination = readPagination(data?.data?.pagination, list.length);

            setOpenRequests(list);
            setOpenRequestsPagination(pagination);
            setStats(prev => ({ ...prev, openRequests: pagination.totalItems, totalRequests: prev.myRequests + pagination.totalItems + prev.directRequests }));
        } catch (err) {
            console.error('Public requests fetch error:', err);
            setOpenRequests([]);
            setOpenRequestsPagination(emptyPagination());
        } finally {
            setLoading(false);
        }
    }, [baseUrl, clinicId, requestDateFrom, requestDateTo, requestPageSize, requestPriorityFilter, requestSearch, requestStatusFilter]);

    const fetchDirectRequests = useCallback(async (page: number) => {
        if (!clinicId) return;

        setLoading(true);
        try {
            const params = new URLSearchParams({
                clinic_id: clinicId.toString(),
                exclude_applied: 'true',
                page: page.toString(),
                page_size: requestPageSize.toString(),
                search: requestSearch,
                status: requestStatusFilter,
                priority: requestPriorityFilter,
                date_from: requestDateFrom,
                date_to: requestDateTo,
                sort_by: 'created',
                sort_direction: 'desc'
            });
            const response = await fetch(buildApiUrl(baseUrl, `collaborations/private-requests/?${params.toString()}`));
            const data = response.ok ? await response.json() : null;
            const list = data?.data?.requests || [];
            const pagination = readPagination(data?.data?.pagination, list.length);

            setDirectRequests(list);
            setDirectRequestsPagination(pagination);
            setStats(prev => ({ ...prev, directRequests: pagination.totalItems, totalRequests: prev.myRequests + prev.openRequests + pagination.totalItems }));
        } catch (err) {
            console.error('Direct requests fetch error:', err);
            setDirectRequests([]);
            setDirectRequestsPagination(emptyPagination());
        } finally {
            setLoading(false);
        }
    }, [baseUrl, clinicId, requestDateFrom, requestDateTo, requestPageSize, requestPriorityFilter, requestSearch, requestStatusFilter]);

    const fetchApplications = useCallback(async () => {
        if (!clinicId) return;

        setLoading(true);
        try {
            const receivedParams = new URLSearchParams({
                clinic_id: clinicId.toString(),
                page: receivedAppsPagination.currentPage.toString(),
                page_size: applicationPageSize.toString(),
                search: applicationSearch,
                status: applicationStatusFilter,
                date_from: applicationDateFrom,
                date_to: applicationDateTo,
                sort_by: 'created',
                sort_direction: 'desc'
            });
            const sentParams = new URLSearchParams({
                clinic_id: clinicId.toString(),
                page: sentAppsPagination.currentPage.toString(),
                page_size: applicationPageSize.toString(),
                search: applicationSearch,
                status: applicationStatusFilter,
                date_from: applicationDateFrom,
                date_to: applicationDateTo,
                sort_by: 'created',
                sort_direction: 'desc'
            });
            const [receivedRes, sentRes] = await Promise.all([
                fetch(buildApiUrl(baseUrl, `collaborations/applications/received/?${receivedParams.toString()}`)),
                fetch(buildApiUrl(baseUrl, `collaborations/applications/sent/?${sentParams.toString()}`))
            ]);

            const receivedData = receivedRes.ok ? await receivedRes.json() : null;
            const sentData = sentRes.ok ? await sentRes.json() : null;
            const normalizedReceived = (receivedData?.data?.applications || []).map(normalizeApplication);
            const normalizedSent = (sentData?.data?.applications || []).map(normalizeApplication);
            const receivedPagination = readPagination(receivedData?.data?.pagination, normalizedReceived.length);
            const sentPagination = readPagination(sentData?.data?.pagination, normalizedSent.length);

            setReceivedApps(normalizedReceived);
            setSentApps(normalizedSent);
            setReceivedAppsPagination(receivedPagination);
            setSentAppsPagination(sentPagination);
            setStats(prev => ({
                ...prev,
                receivedApps: receivedPagination.totalItems,
                sentApps: sentPagination.totalItems,
                negotiations: normalizedReceived.filter((application: Application) => application.status === 'negotiating').length
            }));
        } catch (err) {
            console.error('Applications fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, [baseUrl, clinicId]);

    const refreshCurrentView = useCallback(() => {
        refreshCountsAndPreviews();

        if (tab === 'my-requests') {
            fetchMyRequests(myRequestsPagination.currentPage);
        } else if (tab === 'browse') {
            fetchOpenRequests(openRequestsPagination.currentPage);
        } else if (tab === 'direct') {
            fetchDirectRequests(directRequestsPagination.currentPage);
        } else if (tab === 'applications') {
            fetchApplications();
        }
    }, [directRequestsPagination.currentPage, fetchApplications, fetchDirectRequests, fetchMyRequests, fetchOpenRequests, myRequestsPagination.currentPage, openRequestsPagination.currentPage, refreshCountsAndPreviews, tab]);

    useEffect(() => {
        if (!clinicId) return;
        refreshCountsAndPreviews();
    }, [clinicId, refreshCountsAndPreviews]);

    useEffect(() => {
        if (!clinicId) return;

        if (tab === 'my-requests') {
            fetchMyRequests(myRequestsPagination.currentPage);
        } else if (tab === 'browse') {
            fetchOpenRequests(openRequestsPagination.currentPage);
        } else if (tab === 'direct') {
            fetchDirectRequests(directRequestsPagination.currentPage);
        } else if (tab === 'applications') {
            fetchApplications();
        }
    }, [appTab, applicationDateFrom, applicationDateTo, applicationSearch, applicationStatusFilter, clinicId, directRequestsPagination.currentPage, fetchApplications, fetchDirectRequests, fetchMyRequests, fetchOpenRequests, myRequestsPagination.currentPage, openRequestsPagination.currentPage, requestDateFrom, requestDateTo, requestPriorityFilter, requestSearch, requestStatusFilter, receivedAppsPagination.currentPage, sentAppsPagination.currentPage, tab]);

    /* ─── Fetch paginated services ─── */
    const fetchPaginatedServices = useCallback(async (page = 1, search = '') => {
        if (!baseUrl) return;
        setServicesLoading(true);
        try {
            const params = new URLSearchParams({ page: String(page), page_size: '20' });
            if (search) params.set('search', search);
            const res = await fetch(buildApiUrl(baseUrl, `facilities/services/paginated/?${params}`));
            if (res.ok) {
                const data = await res.json();
                setAvailableServices(data.data || []);
                if (data.pagination) setServicesPagination(readPagination(data.pagination, (data.data || []).length));
            }
        } catch {} finally { setServicesLoading(false); }
    }, [baseUrl]);

    useEffect(() => { fetchPaginatedServices(servicesPage, servicesSearch); }, [servicesPage, servicesSearch, fetchPaginatedServices]);

    useEffect(() => {
        if (!clinicId || !baseUrl) return;
        setClinicServicesLoaded(false);
        (async () => {
            try {
                const response = await fetch(buildApiUrl(baseUrl, `facilities/services/clinic-services/${clinicId}/`));
                if (!response.ok) {
                    setClinicServiceIds([]);
                    return;
                }
                const data = await response.json();
                const ids = (data?.data?.services || [])
                    .map((service: any) => Number(service.service_id))
                    .filter((serviceId: number) => Number.isFinite(serviceId));
                setClinicServiceIds(ids);
            } catch {
                setClinicServiceIds([]);
            } finally {
                setClinicServicesLoaded(true);
            }
        })();
    }, [baseUrl, clinicId]);

    /* ─── Fetch form data (partners) ─── */
    useEffect(() => {
        if (!clinicId || !baseUrl) return;
        (async () => {
            try {
                const pRes = await fetch(buildApiUrl(baseUrl, "facilities/clinics/all"));
                if (pRes.ok) {
                    const pData = await pRes.json();
                    setAvailablePartners((pData.data || []).filter((p: any) => p.id !== clinicId));
                }
            } catch {}
        })();
    }, [clinicId, baseUrl]);

    /* ─── Fetch patients when modal opens or clinicId available ─── */
    useEffect(() => {
        if (!clinicId || !baseUrl) return;
        if (!showCreate && clinicPatients.length > 0) return;
        (async () => {
            try {
                setPatientsLoading(true);
                const ptRes = await fetch(buildApiUrl(baseUrl, `facilities/patients/list?clinic_id=${clinicId}`));
                if (ptRes.ok) {
                    const ptData = await ptRes.json();
                    setClinicPatients(ptData.data || []);
                }
            } catch (err) {
                console.error('Patient fetch error:', err);
            } finally { setPatientsLoading(false); }
        })();
    }, [clinicId, baseUrl, showCreate]);

    /* ─── Create request ─── */
    const CreateServiceRequest = async () => {
        if (!createForm.title || !createForm.description || !createForm.start_date || createForm.required_services.length === 0) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Please fill in title, description, start date, and select at least one service.' });
            return;
        }
        try {
            const res = await fetch(buildApiUrl(baseUrl, "collaborations/create-comprehensive-request/"), {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...createForm, creator_id: clinicId, clinic_id: clinicId,
                    patient_ids: createForm.patient_ids,
                    patient_id: createForm.patient_ids[0] || null,
                    min_pay: createForm.min_pay ? parseInt(createForm.min_pay) : null,
                    max_pay: createForm.max_pay ? parseInt(createForm.max_pay) : null,
                })
            });
            const data = await res.json();
            if (data.status === 'success') {
                Swal.fire({ icon: 'success', title: 'Created', text: 'Service request created successfully.', timer: 2000, showConfirmButton: false });
                setShowCreate(false);
                resetCreateForm();
                refreshCurrentView();
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: data.message || 'Failed to create request.' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Network error. Please try again.' });
        }
    };

    const resetCreateForm = () => {
        setCreateStep(1);
        setServicesSearch('');
        setServicesPage(1);
        setCreateForm({
            title: '', description: '', request_type: 'open', priority: 'normal',
            patient_ids: [], engagement_type: 'consultation', start_date: '', end_date: '',
            response_time: 48, pay_mode: 'milestone', min_pay: '', max_pay: '',
            share_patient_demographics: true, share_medical_history: true,
            share_current_medications: true, share_lab_results: true,
            share_imaging: true, share_notes: true,
            required_services: [], direct_partners: [],
            contract_duration: 'medium_term', contract_term: 'term', renewable: false,
            patient_consent_notes: ''
        });
    };

    const resetApplyForm = () => {
        setApplyStep(1);
        setApplyForm(createEmptyApplyForm());
        setApplyCertificationDraft('');
        setApplyEquipmentDraft('');
        setApplyAttachments([]);
    };

    /* ─── Submit application ─── */
    const submitApplication = async () => {
        if (!showApply || !clinicId) return;
        if (!applyForm.partner_minimum || !applyForm.partner_qualifications) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Please fill in your proposed rate and qualifications.' });
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(buildApiUrl(baseUrl, "collaborations/applications/"), {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    service_request_id: showApply.id,
                    partner_applying_id: clinicId,
                    partner_minimum: parseInt(applyForm.partner_minimum),
                    partner_maximum: parseInt(applyForm.partner_maximum) || parseInt(applyForm.partner_minimum),
                    pay_mode: applyForm.pay_mode,
                    patient_flow: parseInt(applyForm.patient_flow) || 5,
                    patient_flow_rate: applyForm.patient_flow_rate,
                    partner_qualifications: applyForm.partner_qualifications,
                    partner_experience_years: parseInt(applyForm.partner_experience_years) || 1,
                    partner_certifications: applyForm.partner_certifications,
                    partner_equipment: applyForm.partner_equipment,
                    application_notes: applyForm.application_notes
                })
            });
            const data = await res.json();
            if (data.status === 'success' || res.ok) {
                const validAttachments = applyAttachments.filter(a => a.file && a.label.trim());
                if (validAttachments.length > 0 && data.data?.application_id) {
                    const formData = new FormData();
                    validAttachments.forEach(att => {
                        formData.append('files', att.file!);
                        formData.append('labels', att.label.trim());
                    });
                    await fetch(buildApiUrl(baseUrl, `collaborations/applications/${data.data.application_id}/documents/`), {
                        method: 'POST', body: formData
                    });
                }
                Swal.fire({ icon: 'success', title: 'Application Submitted!', text: 'Your application was submitted successfully. Redirecting to your applications...', timer: 2500, showConfirmButton: false, didOpen: () => { Swal.showLoading(); } });
                setOpenRequests(prev => prev.filter(request => request.id !== showApply.id));
                setDirectRequests(prev => prev.filter(request => request.id !== showApply.id));
                window.dispatchEvent(new CustomEvent('uzima:inbox-updated'));
                setShowApply(null);
                resetApplyForm();
                setTimeout(() => { setTab('applications'); setAppTab('sent'); refreshCurrentView(); }, 1200);
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: data.message || 'Failed to submit application.' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Network error. Please try again.' });
        } finally {
            setSubmitting(false);
        }
    };

    /* ─── Open application detail ─── */
    const openApplicationDetail = async (appId: number) => {
        setViewingAppLoading(true);
        setViewingApplication(null);
        setAmendMode(false);
        try {
            const res = await fetch(buildApiUrl(baseUrl, `collaborations/applications/${appId}/detail/`));
            const data = await res.json();
            if (data.status === 'success') {
                setViewingApplication(data.data);
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: data.message || 'Failed to load application details.' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not load application details.' });
        } finally {
            setViewingAppLoading(false);
        }
    };

    /* ─── Amend application ─── */
    const submitAmendment = async () => {
        if (!viewingApplication || !clinicId) return;
        setAmendSubmitting(true);
        try {
            const appId = viewingApplication.application.id;
            const res = await fetch(buildApiUrl(baseUrl, `collaborations/applications/${appId}/`), {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: 'submitted', clinic_id: clinicId, ...amendForm })
            });
            if (res.ok) {
                Swal.fire({ icon: 'success', title: 'Amended', text: 'Your application has been updated.', timer: 1500, showConfirmButton: false });
                setAmendMode(false);
                openApplicationDetail(appId);
                refreshCurrentView();
            } else {
                const d = await res.json();
                Swal.fire({ icon: 'error', title: 'Error', text: d.message || 'Failed to amend application.' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Network error.' });
        } finally {
            setAmendSubmitting(false);
        }
    };

    /* ─── Send negotiation message ─── */
    const sendNegotiationMessage = async (msgType: string = 'question') => {
        if (!viewingApplication || !clinicId || !negotiationMsg.trim()) return;
        const app = viewingApplication.application;
        const recipientId = app.applicant.id === clinicId
            ? viewingApplication.service_request.creator.id
            : app.applicant.id;
        try {
            const res = await fetch(buildApiUrl(baseUrl, `collaborations/applications/${app.id}/negotiate/`), {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sender_id: clinicId, recipient_id: recipientId, message: negotiationMsg, message_type: msgType })
            });
            if (res.ok) {
                setNegotiationMsg('');
                openApplicationDetail(app.id);
                window.dispatchEvent(new CustomEvent('uzima:inbox-updated'));
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to send message.' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Network error.' });
        }
    };

    /* ─── Application Actions (Accept / Reject / Withdraw) ─── */
    const updateApplicationStatus = async (appId: number, newStatus: string, confirmTitle: string, confirmText: string) => {
        const result = await Swal.fire({
            icon: 'warning', title: confirmTitle, text: confirmText,
            showCancelButton: true, confirmButtonText: 'Yes, proceed', confirmButtonColor: '#0f766e'
        });
        if (!result.isConfirmed || !clinicId) return;
        try {
            const res = await fetch(buildApiUrl(baseUrl, `collaborations/applications/${appId}/`), {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus, clinic_id: clinicId })
            });
            const d = await res.json();
            if (res.ok || d.success) {
                Swal.fire({ icon: 'success', title: 'Done', text: d.message || `Application ${newStatus.replace('_', ' ')}.`, timer: 1800, showConfirmButton: false });
                openApplicationDetail(appId);
                refreshCurrentView();
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: d.message || 'Action failed.' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Network error.' });
        }
    };

    const withdrawApplication = async (appId: number) => {
        const result = await Swal.fire({
            icon: 'warning', title: 'Withdraw Application?', text: 'This will retract your application. You cannot undo this.',
            showCancelButton: true, confirmButtonText: 'Withdraw', confirmButtonColor: '#dc2626'
        });
        if (!result.isConfirmed || !clinicId) return;
        try {
            const res = await fetch(buildApiUrl(baseUrl, `collaborations/applications/${appId}/`), {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ withdrawn: true, clinic_id: clinicId })
            });
            const d = await res.json();
            if (res.ok || d.success) {
                Swal.fire({ icon: 'success', title: 'Withdrawn', text: 'Your application has been withdrawn.', timer: 1800, showConfirmButton: false });
                openApplicationDetail(appId);
                refreshCurrentView();
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: d.message || 'Withdraw failed.' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Network error.' });
        }
    };

    const upgradeToCollaboration = async (serviceRequestId: number) => {
        const result = await Swal.fire({
            icon: 'question', title: 'Create Collaboration?', text: 'This will convert the agreed service request into an active collaboration.',
            showCancelButton: true, confirmButtonText: 'Create Collaboration', confirmButtonColor: '#0f766e'
        });
        if (!result.isConfirmed) return;
        try {
            const res = await fetch(buildApiUrl(baseUrl, `collaborations/service-requests/${serviceRequestId}/upgrade/`), {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ clinic_id: clinicId })
            });
            const d = await res.json();
            if (res.ok || d.success) {
                Swal.fire({ icon: 'success', title: 'Collaboration Created', text: 'The service request has been upgraded to a collaboration.', timer: 2000, showConfirmButton: false });
                if (d.data?.collaboration_id) {
                    navigate(`/dashboard/collaborations/${d.data.collaboration_id}`);
                }
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: d.message || 'Could not create collaboration.' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Network error.' });
        }
    };

    /* ─── Render helpers ─── */
    const StatusBadge = ({ status }: { status: string }) => (
        <span className={`sc-badge-status ${status}`}>{status.replace('_', ' ')}</span>
    );

    const PriorityBadge = ({ priority }: { priority: string }) => (
        <span className={`sc-badge-priority ${priority}`}>{priority}</span>
    );

    const TypeBadge = ({ type }: { type: string }) => (
        <span className={`sc-badge-type ${type}`}>{type === 'open' ? 'Public' : 'Direct'}</span>
    );

    const SetActiveRequestPage = (page: number) => {
        if (tab === 'my-requests') {
            setMyRequestsPagination((prev: PaginationMeta) => ({ ...prev, currentPage: page }));
        } else if (tab === 'browse') {
            setOpenRequestsPagination((prev: PaginationMeta) => ({ ...prev, currentPage: page }));
        } else if (tab === 'direct') {
            setDirectRequestsPagination((prev: PaginationMeta) => ({ ...prev, currentPage: page }));
        }
    };

    const ResetActiveRequestPage = () => {
        SetActiveRequestPage(1);
    };

    const SetActiveApplicationPage = (page: number) => {
        if (appTab === 'received') {
            setReceivedAppsPagination(prev => ({ ...prev, currentPage: page }));
        } else {
            setSentAppsPagination(prev => ({ ...prev, currentPage: page }));
        }
    };

    const ResetActiveApplicationPage = () => {
        SetActiveApplicationPage(1);
    };

    const RequestCard = ({ r, showActions }: { r: ServiceRequest; showActions?: 'browse' | 'mine' | 'direct' }) => (
        <div className="sc-card" key={r.id}>
            <div className="sc-card-body">
                <div className="sc-card-row">
                    <div style={{ flex: 1 }}>
                        <h5 className="sc-card-title">{r.title}</h5>
                        <p className="sc-card-desc">{r.description}</p>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <TypeBadge type={r.request_type} />
                        <PriorityBadge priority={r.priority} />
                    </div>
                </div>
                <div className="sc-card-meta">
                    <span><i className="bx bx-buildings"></i> {r.creator?.name || r.clinic_name || '—'}</span>
                    <span><i className="bx bx-calendar"></i> {fmtDate(r.start_date)}</span>
                    <span><i className="bx bx-money"></i> {fmtPay(r.min_pay, r.max_pay)}</span>
                    {r.response_time && <span><i className="bx bx-time"></i> {r.response_time}h response</span>}
                </div>
            </div>
            <div className="sc-card-foot">
                <StatusBadge status={r.status} />
                <div style={{ display: 'flex', gap: 6 }}>
                    <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => setDetailRequest(r)}>
                        <i className="bx bx-show"></i> View
                    </button>
                    {(showActions === 'browse' || showActions === 'direct') && r.creator?.id !== clinicId && (
                        <button className="sc-btn sc-btn-primary sc-btn-sm" onClick={() => { resetApplyForm(); setShowApply(r); }}>
                            <i className="bx bx-send"></i> Apply
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

    const RequestToolbar = () => (
        <div className="sc-toolbar">
            <div className="sc-search">
                <i className="bx bx-search"></i>
                <input placeholder="Search requests..." value={requestSearch} onChange={e => { setRequestSearch(e.target.value); ResetActiveRequestPage(); }} />
            </div>
            <select className="sc-select" value={requestStatusFilter} onChange={e => { setRequestStatusFilter(e.target.value); ResetActiveRequestPage(); }}>
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="pending">Pending</option>
                <option value="reviewing">Reviewing</option>
                <option value="negotiating">Negotiating</option>
                <option value="agreed">Agreed</option>
                <option value="rejected">Rejected</option>
            </select>
            <select className="sc-select" value={requestPriorityFilter} onChange={e => { setRequestPriorityFilter(e.target.value); ResetActiveRequestPage(); }}>
                <option value="">All Priorities</option>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
            </select>
            <input className="sc-input" type="date" value={requestDateFrom} onChange={e => { setRequestDateFrom(e.target.value); ResetActiveRequestPage(); }} style={{ maxWidth: 160 }} />
            <input className="sc-input" type="date" value={requestDateTo} onChange={e => { setRequestDateTo(e.target.value); ResetActiveRequestPage(); }} style={{ maxWidth: 160 }} />
            <div style={{ display: 'flex', gap: 6 }}>
                <button className={`sc-btn sc-btn-sm ${requestViewMode === 'grid' ? 'sc-btn-primary' : 'sc-btn-outline'}`} onClick={() => setRequestViewMode('grid')}>
                    <i className="bx bx-grid-alt"></i>
                </button>
                <button className={`sc-btn sc-btn-sm ${requestViewMode === 'list' ? 'sc-btn-primary' : 'sc-btn-outline'}`} onClick={() => setRequestViewMode('list')}>
                    <i className="bx bx-list-ul"></i>
                </button>
            </div>
        </div>
    );

    const ApplicationToolbar = () => (
        <div className="sc-toolbar">
            <div className="sc-search">
                <i className="bx bx-search"></i>
                <input placeholder="Search applications..." value={applicationSearch} onChange={e => { setApplicationSearch(e.target.value); ResetActiveApplicationPage(); }} />
            </div>
            <select className="sc-select" value={applicationStatusFilter} onChange={e => { setApplicationStatusFilter(e.target.value); ResetActiveApplicationPage(); }}>
                <option value="">All Statuses</option>
                <option value="submitted">Submitted</option>
                <option value="under_review">Under review</option>
                <option value="negotiating">Negotiating</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
                <option value="agreed">Agreed</option>
            </select>
            <input className="sc-input" type="date" value={applicationDateFrom} onChange={e => { setApplicationDateFrom(e.target.value); ResetActiveApplicationPage(); }} style={{ maxWidth: 160 }} />
            <input className="sc-input" type="date" value={applicationDateTo} onChange={e => { setApplicationDateTo(e.target.value); ResetActiveApplicationPage(); }} style={{ maxWidth: 160 }} />
            <div style={{ display: 'flex', gap: 6 }}>
                <button className={`sc-btn sc-btn-sm ${applicationViewMode === 'grid' ? 'sc-btn-primary' : 'sc-btn-outline'}`} onClick={() => setApplicationViewMode('grid')}>
                    <i className="bx bx-grid-alt"></i>
                </button>
                <button className={`sc-btn sc-btn-sm ${applicationViewMode === 'list' ? 'sc-btn-primary' : 'sc-btn-outline'}`} onClick={() => setApplicationViewMode('list')}>
                    <i className="bx bx-list-ul"></i>
                </button>
            </div>
        </div>
    );

    const PaginationControls = ({ pagination, label, MovePage }: { pagination: PaginationMeta; label: string; MovePage: (page: number) => void }) => {
        if (pagination.totalPages <= 1) return null;

        return (
            <div className="sc-card-foot" style={{ marginTop: 18, justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--uzima-shell-text-soft)' }}>
                    Page {pagination.currentPage} of {pagination.totalPages} · {pagination.totalItems} {label}{pagination.totalItems === 1 ? '' : 's'}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button className="sc-btn sc-btn-ghost sc-btn-sm" disabled={!pagination.hasPrevious} onClick={() => MovePage(pagination.currentPage - 1)}>
                        <i className="bx bx-chevron-left"></i> Previous
                    </button>
                    <button className="sc-btn sc-btn-ghost sc-btn-sm" disabled={!pagination.hasNext} onClick={() => MovePage(pagination.currentPage + 1)}>
                        Next <i className="bx bx-chevron-right"></i>
                    </button>
                </div>
            </div>
        );
    };

    const renderRequestList = (list: ServiceRequest[], pagination: PaginationMeta, emptyIcon: string, emptyTitle: string, emptyDesc: string, showActions: 'browse' | 'mine' | 'direct', action?: () => void) => (
        <>
            <RequestToolbar />
            {list.length === 0 ? (
                <EmptyState icon={emptyIcon} title={emptyTitle} desc={emptyDesc} action={action} />
            ) : (
                <>
                    <div className={requestViewMode === 'grid' ? 'sc-grid' : 'sc-list-stack'}>{list.map((r: ServiceRequest) => <RequestCard key={r.id} r={r} showActions={showActions} />)}</div>
                    <PaginationControls pagination={pagination} label="request" MovePage={SetActiveRequestPage} />
                </>
            )}
        </>
    );

    const EmptyState = ({ icon, title, desc, action }: { icon: string; title: string; desc: string; action?: () => void }) => (
        <div className="sc-empty">
            <i className={icon}></i>
            <h3>{title}</h3>
            <p>{desc}</p>
            {action && <button className="sc-btn sc-btn-primary" onClick={action}><i className="bx bx-plus"></i> Create Request</button>}
        </div>
    );

    /* ─── Tab: Overview ─── */
    const renderOverview = () => (
        <>
            <div className="sc-stats">
                <div className="sc-stat">
                    <div className="sc-stat-icon teal"><i className="bx bx-file"></i></div>
                    <div className="sc-stat-value">{stats.myRequests}</div>
                    <div className="sc-stat-label">My Requests</div>
                </div>
                <div className="sc-stat">
                    <div className="sc-stat-icon blue"><i className="bx bx-globe"></i></div>
                    <div className="sc-stat-value">{stats.openRequests}</div>
                    <div className="sc-stat-label">Public Requests</div>
                </div>
                <div className="sc-stat">
                    <div className="sc-stat-icon amber"><i className="bx bx-mail-send"></i></div>
                    <div className="sc-stat-value">{stats.directRequests}</div>
                    <div className="sc-stat-label">Direct Requests</div>
                </div>
                <div className="sc-stat">
                    <div className="sc-stat-icon amber"><i className="bx bx-inbox"></i></div>
                    <div className="sc-stat-value">{stats.receivedApps}</div>
                    <div className="sc-stat-label">Received Applications</div>
                </div>
                <div className="sc-stat">
                    <div className="sc-stat-icon violet"><i className="bx bx-paper-plane"></i></div>
                    <div className="sc-stat-value">{stats.sentApps}</div>
                    <div className="sc-stat-label">Sent Applications</div>
                </div>
                <div className="sc-stat">
                    <div className="sc-stat-icon rose"><i className="bx bx-conversation"></i></div>
                    <div className="sc-stat-value">{stats.negotiations}</div>
                    <div className="sc-stat-label">Active Negotiations</div>
                </div>
            </div>

            {/* Quick Actions */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
                <button className="sc-btn sc-btn-primary" onClick={() => setShowCreate(true)}>
                    <i className="bx bx-plus"></i> New Service Request
                </button>
                <button className="sc-btn sc-btn-outline" onClick={() => setTab('browse')}>
                    <i className="bx bx-search-alt"></i> Browse Open Requests
                </button>
                <button className="sc-btn sc-btn-outline" onClick={() => setTab('direct')}>
                    <i className="bx bx-mail-send"></i> View Direct Requests
                </button>
                <button className="sc-btn sc-btn-outline" onClick={() => setTab('applications')}>
                    <i className="bx bx-inbox"></i> View Applications
                </button>
            </div>

            {/* Recent Requests */}
            <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>Recent Requests</h3>
                    {myRequests.length > 0 && (
                        <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => setTab('my-requests')}>View All</button>
                    )}
                </div>
                {myRequests.length === 0 ? (
                    <EmptyState
                        icon="bx bx-file"
                        title="No requests yet"
                        desc="Create your first service request to get started."
                        action={() => setShowCreate(true)}
                    />
                ) : (
                    <div className="sc-grid">
                        {myRequests.slice(0, 4).map(r => <RequestCard key={r.id} r={r} showActions="mine" />)}
                    </div>
                )}
            </div>

            {/* Available from others */}
            {openRequests.length > 0 && (
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>Public Requests from Other Clinics</h3>
                        <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => setTab('browse')}>View All</button>
                    </div>
                    <div className="sc-grid">
                        {openRequests.slice(0, 4).map(r => <RequestCard key={r.id} r={r} showActions="browse" />)}
                    </div>
                </div>
            )}

            {directRequests.length > 0 && (
                <div style={{ marginTop: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>Direct Requests Sent to You</h3>
                        <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => setTab('direct')}>View All</button>
                    </div>
                    <div className="sc-grid">
                        {directRequests.slice(0, 4).map(r => <RequestCard key={r.id} r={r} showActions="direct" />)}
                    </div>
                </div>
            )}
        </>
    );

    /* ─── Tab: My Requests ─── */
    const renderMyRequests = () => renderRequestList(myRequests, myRequestsPagination, 'bx bx-file', 'No requests found', 'Create a new service request to find partners.', 'mine', () => setShowCreate(true));

    /* ─── Tab: Browse ─── */
    const renderBrowse = () => renderRequestList(openRequests, openRequestsPagination, 'bx bx-globe', 'No public requests', 'There are no public service requests from other clinics available right now.', 'browse');

    const renderDirect = () => renderRequestList(directRequests, directRequestsPagination, 'bx bx-mail-send', 'No direct requests', 'No private service requests are currently directed to your clinic.', 'direct');

    /* ─── Tab: Applications ─── */
    const renderApplications = () => {
        const list = appTab === 'received' ? receivedApps : sentApps;
        const pagination = appTab === 'received' ? receivedAppsPagination : sentAppsPagination;

        return (
            <>
                <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
                    <button className={`sc-btn ${appTab === 'received' ? 'sc-btn-primary' : 'sc-btn-outline'} sc-btn-sm`} onClick={() => setAppTab('received')}>
                        <i className="bx bx-inbox"></i> Received <span className="sc-badge">{stats.receivedApps}</span>
                    </button>
                    <button className={`sc-btn ${appTab === 'sent' ? 'sc-btn-primary' : 'sc-btn-outline'} sc-btn-sm`} onClick={() => setAppTab('sent')}>
                        <i className="bx bx-paper-plane"></i> Sent <span className="sc-badge">{stats.sentApps}</span>
                    </button>
                </div>

                <ApplicationToolbar />

                {list.length === 0 ? (
                    <EmptyState icon="bx bx-envelope" title={`No ${appTab} applications`} desc={appTab === 'received' ? 'No partners have applied to your requests yet.' : 'You have not applied to any requests yet.'} />
                ) : (
                    <>
                    <div className={applicationViewMode === 'grid' ? 'sc-grid' : 'sc-list-stack'}>
                        {list.map((a: Application) => (
                            <div className="sc-card sc-card-clickable" key={a.id} onClick={() => openApplicationDetail(a.id)}>
                                <div className="sc-card-body">
                                    <div className="sc-app-partner">
                                        <div className="sc-app-avatar">
                                            {(appTab === 'received' ? a.applicant?.name : a.service_request?.creator_name || 'P')?.charAt(0)?.toUpperCase() || 'P'}
                                        </div>
                                        <div>
                                            <div className="sc-app-name">{appTab === 'received' ? a.applicant?.name || 'Partner' : a.service_request?.creator_name || 'Creator'}</div>
                                            <div className="sc-app-role">{appTab === 'received' ? 'Applied to your request' : 'Your application'}</div>
                                        </div>
                                    </div>
                                    <h5 className="sc-card-title">{a.service_request?.title || 'Service Request'}</h5>
                                    {a.service_request?.description ? <p className="sc-card-desc">{a.service_request.description}</p> : null}
                                    <div className="sc-card-meta">
                                        <span><i className="bx bx-calendar"></i> {fmtDate(a.created)}</span>
                                        {a.partner_minimum && <span><i className="bx bx-money"></i> KES {a.partner_minimum?.toLocaleString()}</span>}
                                    </div>
                                    {a.partner_certifications && a.partner_certifications.length > 0 ? (
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
                                            {a.partner_certifications.map(item => <span key={item} className="sc-chip">{item}</span>)}
                                        </div>
                                    ) : null}
                                    {a.partner_equipment && a.partner_equipment.length > 0 ? (
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                                            {a.partner_equipment.map(item => <span key={item} className="sc-chip soft">{item}</span>)}
                                        </div>
                                    ) : null}
                                </div>
                                <div className="sc-card-foot">
                                    <StatusBadge status={a.status} />
                                    <div style={{ display: 'flex', gap: 6 }}>
                                        {appTab === 'received' && a.status === 'submitted' && (
                                            <>
                                                <button className="sc-btn sc-btn-primary sc-btn-sm" onClick={() => UpdateApplicationDecision(a.id, 'accept')}><i className="bx bx-check"></i> Accept</button>
                                                <button className="sc-btn sc-btn-danger sc-btn-sm" onClick={() => UpdateApplicationDecision(a.id, 'reject')}><i className="bx bx-x"></i> Reject</button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <PaginationControls pagination={pagination} label="application" MovePage={SetActiveApplicationPage} />
                    </>
                )}
            </>
        );
    };

    const UpdateApplicationDecision = async (appId: number, action: 'accept' | 'reject') => {
        const confirm = await Swal.fire({
            title: `${action === 'accept' ? 'Accept' : 'Reject'} Application?`,
            text: `Are you sure you want to ${action} this application?`,
            icon: 'question', showCancelButton: true, confirmButtonColor: action === 'accept' ? '#0f766e' : '#e11d48',
            confirmButtonText: `Yes, ${action}`
        });
        if (!confirm.isConfirmed) return;
        try {
            const res = await fetch(buildApiUrl(baseUrl, `collaborations/applications/${appId}/`), {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: action === 'accept' ? 'accepted' : 'rejected', clinic_id: clinicId })
            });
            if (res.ok) {
                Swal.fire({ icon: 'success', title: action === 'accept' ? 'Accepted' : 'Rejected', timer: 1500, showConfirmButton: false });
                window.dispatchEvent(new CustomEvent('uzima:inbox-updated'));
                refreshCurrentView();
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update application.' });
        }
    };

    /* ─── Create Modal ─── */
    const renderCreateModal = () => {
        const steps = ['Basics', 'Services', 'Details', 'Sharing', 'Review'];
        const updateForm = (field: string, val: any) => setCreateForm(prev => ({ ...prev, [field]: val }));

        return (
            <div className="sc-overlay" onClick={() => setShowCreate(false)}>
                <div className="sc-modal sc-modal-lg" onClick={e => e.stopPropagation()}>
                    <div className="sc-modal-header">
                        <h2>Create Service Request</h2>
                        <button className="sc-modal-close" onClick={() => setShowCreate(false)}><i className="bx bx-x"></i></button>
                    </div>

                    {/* Step indicator */}
                    <div className="sc-steps" style={{ paddingTop: 16 }}>
                        {steps.map((s, i) => (
                            <div key={s} className={`sc-step ${createStep === i + 1 ? 'active' : createStep > i + 1 ? 'done' : ''}`}>
                                <div className="sc-step-num">{createStep > i + 1 ? <i className="bx bx-check"></i> : i + 1}</div>
                                <span className="sc-step-label">{s}</span>
                            </div>
                        ))}
                    </div>

                    <div className="sc-modal-body">
                        {createStep === 1 && (
                            <>
                                <div className="sc-form-group">
                                    <label>Title <span>*</span></label>
                                    <input className="sc-input" value={createForm.title} onChange={e => updateForm('title', e.target.value)} placeholder="e.g. Specialist Cardiology Consultation" />
                                </div>
                                <div className="sc-form-group">
                                    <label>Description <span>*</span></label>
                                    <textarea className="sc-textarea" value={createForm.description} onChange={e => updateForm('description', e.target.value)} placeholder="Describe the service you need in detail..." rows={4} />
                                </div>
                                <div className="sc-form-row">
                                    <div className="sc-form-group">
                                        <label>Request Type</label>
                                        <select className="sc-input" value={createForm.request_type} onChange={e => updateForm('request_type', e.target.value)}>
                                            <option value="open">Public — Any partner can apply</option>
                                            <option value="direct">Direct — Send to specific partners</option>
                                        </select>
                                    </div>
                                    <div className="sc-form-group">
                                        <label>Priority</label>
                                        <select className="sc-input" value={createForm.priority} onChange={e => updateForm('priority', e.target.value)}>
                                            <option value="low">Low</option>
                                            <option value="normal">Normal</option>
                                            <option value="high">High</option>
                                            <option value="urgent">Urgent</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="sc-form-row">
                                    <div className="sc-form-group">
                                        <label>Engagement Type</label>
                                        <select className="sc-input" value={createForm.engagement_type} onChange={e => updateForm('engagement_type', e.target.value)}>
                                            <option value="consultation">Consultation</option>
                                            <option value="referral">Referral</option>
                                            <option value="lab_work">Lab Work</option>
                                            <option value="imaging">Imaging</option>
                                            <option value="surgery">Surgery</option>
                                            <option value="therapy">Therapy</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                    <div className="sc-form-group" style={{ flex: '1 1 100%' }}>
                                        <label>Attach Patients</label>
                                        <div className="sc-patient-picker">
                                            <div className="sc-search" style={{ marginBottom: 8 }}>
                                                <i className="bx bx-search"></i>
                                                <input placeholder="Search patients by name or ID..." value={patientSearch} onChange={e => setPatientSearch(e.target.value)} />
                                            </div>
                                            {createForm.patient_ids.length > 0 && (
                                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                                                    {createForm.patient_ids.map(pid => {
                                                        const pt = clinicPatients.find(p => p.patient_id === pid);
                                                        return (
                                                            <span key={pid} className="sc-badge-type direct" style={{ padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                                {pt ? `${pt.first_name} ${pt.last_name}` : pid}
                                                                <i className="bx bx-x" style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                                                                    onClick={() => updateForm('patient_ids', createForm.patient_ids.filter((id: string) => id !== pid))} />
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid var(--uzima-shell-border)', borderRadius: 10, padding: 4 }}>
                                                {patientsLoading ? (
                                                    <div style={{ padding: 16, textAlign: 'center', color: 'var(--uzima-shell-text-soft)', fontSize: '0.8rem' }}>Loading patients...</div>
                                                ) : (() => {
                                                    const filtered = clinicPatients.filter(p => {
                                                        if (!patientSearch) return true;
                                                        const q = patientSearch.toLowerCase();
                                                        return p.first_name.toLowerCase().includes(q) || p.last_name.toLowerCase().includes(q) || p.patient_id.toLowerCase().includes(q) || p.email.toLowerCase().includes(q);
                                                    });
                                                    if (filtered.length === 0) return <div style={{ padding: 16, textAlign: 'center', color: 'var(--uzima-shell-text-soft)', fontSize: '0.8rem' }}>{clinicPatients.length === 0 ? 'No patients enrolled in your clinic' : 'No patients match your search'}</div>;
                                                    return filtered.map(p => (
                                                        <div key={p.patient_id} className="sc-toggle" style={{ padding: '8px 10px', borderRadius: 8, cursor: 'pointer' }}
                                                            onClick={() => {
                                                                const ids = createForm.patient_ids.includes(p.patient_id)
                                                                    ? createForm.patient_ids.filter((id: string) => id !== p.patient_id)
                                                                    : [...createForm.patient_ids, p.patient_id];
                                                                updateForm('patient_ids', ids);
                                                            }}>
                                                            <input type="checkbox" checked={createForm.patient_ids.includes(p.patient_id)} readOnly />
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{p.first_name} {p.last_name}</div>
                                                                <div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-soft)' }}>ID: {p.patient_id} · {p.email}</div>
                                                            </div>
                                                        </div>
                                                    ));
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {createStep === 2 && (
                            <>
                                <p style={{ fontSize: '0.84rem', color: 'var(--uzima-shell-text-soft)', marginBottom: 12 }}>
                                    Select the services you need for this request. Only partners offering these services will see your request.
                                </p>
                                <div className="sc-search" style={{ marginBottom: 12 }}>
                                    <i className="bx bx-search"></i>
                                    <input placeholder="Search services..." value={servicesSearch} onChange={e => { setServicesSearch(e.target.value); setServicesPage(1); }} />
                                </div>
                                {createForm.required_services.length > 0 && (
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                                        {createForm.required_services.map((sid: number) => {
                                            const svc = availableServices.find((s: any) => s.id === sid);
                                            return (
                                                <span key={sid} className="sc-badge-type direct" style={{ padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                    {svc ? svc.name : `ID: ${sid}`}
                                                    <i className="bx bx-x" style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                                                        onClick={() => updateForm('required_services', createForm.required_services.filter((id: number) => id !== sid))} />
                                                </span>
                                            );
                                        })}
                                    </div>
                                )}
                                <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid var(--uzima-shell-border)', borderRadius: 10, padding: 4 }}>
                                    {servicesLoading ? (
                                        <div style={{ padding: 24, textAlign: 'center', color: 'var(--uzima-shell-text-soft)' }}>Loading services...</div>
                                    ) : availableServices.length === 0 ? (
                                        <div style={{ padding: 24, textAlign: 'center', color: 'var(--uzima-shell-text-soft)', fontSize: '0.82rem' }}>No services found</div>
                                    ) : availableServices.map((svc: any) => (
                                        <div key={svc.id} className="sc-toggle" style={{ padding: '10px 12px', borderRadius: 8, cursor: 'pointer' }}
                                            onClick={() => {
                                                const ids = createForm.required_services.includes(svc.id)
                                                    ? createForm.required_services.filter((id: number) => id !== svc.id)
                                                    : [...createForm.required_services, svc.id];
                                                updateForm('required_services', ids);
                                            }}>
                                            <input type="checkbox" checked={createForm.required_services.includes(svc.id)} readOnly />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{svc.name}</div>
                                                <div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-soft)' }}>{svc.category_name || svc.subtitle || ''}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {servicesPagination.totalPages > 1 && (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 12 }}>
                                        <button className="sc-btn sc-btn-ghost" disabled={!servicesPagination.hasPrevious} onClick={() => setServicesPage(p => p - 1)} style={{ padding: '4px 12px', fontSize: '0.78rem' }}>Prev</button>
                                        <span style={{ fontSize: '0.78rem', color: 'var(--uzima-shell-text-soft)' }}>Page {servicesPagination.currentPage} of {servicesPagination.totalPages}</span>
                                        <button className="sc-btn sc-btn-ghost" disabled={!servicesPagination.hasNext} onClick={() => setServicesPage(p => p + 1)} style={{ padding: '4px 12px', fontSize: '0.78rem' }}>Next</button>
                                    </div>
                                )}
                            </>
                        )}

                        {createStep === 3 && (
                            <>
                                <div className="sc-form-row">
                                    <div className="sc-form-group">
                                        <label>Start Date <span>*</span></label>
                                        <input className="sc-input" type="date" value={createForm.start_date} onChange={e => updateForm('start_date', e.target.value)} />
                                    </div>
                                    <div className="sc-form-group">
                                        <label>End Date</label>
                                        <input className="sc-input" type="date" value={createForm.end_date} onChange={e => updateForm('end_date', e.target.value)} />
                                    </div>
                                </div>
                                <div className="sc-form-row">
                                    <div className="sc-form-group">
                                        <label>Response Time (hours)</label>
                                        <input className="sc-input" type="number" value={createForm.response_time} onChange={e => updateForm('response_time', parseInt(e.target.value) || 48)} />
                                    </div>
                                    <div className="sc-form-group">
                                        <label>Payment Mode</label>
                                        <select className="sc-input" value={createForm.pay_mode} onChange={e => updateForm('pay_mode', e.target.value)}>
                                            <option value="milestone">Per Milestone</option>
                                            <option value="person">Per Person</option>
                                            <option value="request">Per Request</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="sc-form-row">
                                    <div className="sc-form-group">
                                        <label>Min Budget (KES)</label>
                                        <input className="sc-input" type="number" value={createForm.min_pay} onChange={e => updateForm('min_pay', e.target.value)} placeholder="0" />
                                    </div>
                                    <div className="sc-form-group">
                                        <label>Max Budget (KES)</label>
                                        <input className="sc-input" type="number" value={createForm.max_pay} onChange={e => updateForm('max_pay', e.target.value)} placeholder="0" />
                                    </div>
                                </div>
                                <div className="sc-form-row">
                                    <div className="sc-form-group">
                                        <label>Contract Duration</label>
                                        <select className="sc-input" value={createForm.contract_duration} onChange={e => updateForm('contract_duration', e.target.value)}>
                                            <option value="short_term">Short Term</option>
                                            <option value="medium_term">Medium Term</option>
                                            <option value="long_term">Long Term</option>
                                        </select>
                                    </div>
                                    <div className="sc-form-group">
                                        <label>Contract Type</label>
                                        <select className="sc-input" value={createForm.contract_term} onChange={e => updateForm('contract_term', e.target.value)}>
                                            <option value="term">Term Engagement</option>
                                            <option value="permanent">Permanent Engagement</option>
                                        </select>
                                    </div>
                                </div>
                                {createForm.request_type === 'direct' && (
                                    <div className="sc-form-group">
                                        <label>Target Partners (search by email) <span>*</span></label>
                                        {createForm.direct_partners.length > 0 && (
                                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                                                {createForm.direct_partners.map((pid: number) => {
                                                    const partner = availablePartners.find((p: any) => p.id === pid);
                                                    return (
                                                        <span key={pid} className="sc-badge-type direct" style={{ padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                            {partner ? `${partner.name} (${partner.email})` : `ID: ${pid}`}
                                                            <i className="bx bx-x" style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                                                                onClick={() => updateForm('direct_partners', createForm.direct_partners.filter((id: number) => id !== pid))} />
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        <div className="sc-search" style={{ marginBottom: 8 }}>
                                            <i className="bx bx-search"></i>
                                            <input placeholder="Type partner email address..." value={partnerEmailSearch} onChange={e => setPartnerEmailSearch(e.target.value)} />
                                        </div>
                                        <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid var(--uzima-shell-border)', borderRadius: 10, padding: 4 }}>
                                            {(() => {
                                                const q = partnerEmailSearch.toLowerCase();
                                                const filtered = q.length >= 2
                                                    ? availablePartners.filter((p: any) =>
                                                        (p.email && p.email.toLowerCase().includes(q)) ||
                                                        (p.name && p.name.toLowerCase().includes(q))
                                                    )
                                                    : [];
                                                if (q.length < 2) return <div style={{ padding: 16, textAlign: 'center', color: 'var(--uzima-shell-text-soft)', fontSize: '0.8rem' }}>Type at least 2 characters of a partner email to search</div>;
                                                if (filtered.length === 0) return <div style={{ padding: 16, textAlign: 'center', color: 'var(--uzima-shell-text-soft)', fontSize: '0.8rem' }}>No partners match "{partnerEmailSearch}"</div>;
                                                return filtered.map((p: any) => (
                                                    <div key={p.id} className="sc-toggle" style={{ padding: '8px 10px', borderRadius: 8, cursor: 'pointer' }}
                                                        onClick={() => {
                                                            const ids = createForm.direct_partners.includes(p.id)
                                                                ? createForm.direct_partners.filter((id: number) => id !== p.id)
                                                                : [...createForm.direct_partners, p.id];
                                                            updateForm('direct_partners', ids);
                                                        }}>
                                                        <input type="checkbox" checked={createForm.direct_partners.includes(p.id)} readOnly />
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{p.name}</div>
                                                            <div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-soft)' }}>{p.email}</div>
                                                        </div>
                                                    </div>
                                                ));
                                            })()}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {createStep === 4 && (
                            <>
                                <p style={{ fontSize: '0.84rem', color: 'var(--uzima-shell-text-soft)', marginBottom: 18 }}>
                                    Choose what patient information to share with the service provider.
                                </p>
                                {[
                                    { key: 'share_patient_demographics', label: 'Patient Demographics' },
                                    { key: 'share_medical_history', label: 'Medical History' },
                                    { key: 'share_current_medications', label: 'Current Medications' },
                                    { key: 'share_lab_results', label: 'Lab Results' },
                                    { key: 'share_imaging', label: 'Imaging Reports' },
                                    { key: 'share_notes', label: 'Clinical Notes' },
                                ].map(item => (
                                    <div key={item.key} className="sc-toggle">
                                        <input type="checkbox" checked={(createForm as any)[item.key]}
                                            onChange={() => updateForm(item.key, !(createForm as any)[item.key])} />
                                        <span>{item.label}</span>
                                    </div>
                                ))}
                                <div className="sc-form-group" style={{ marginTop: 18 }}>
                                    <label>Patient Consent Notes</label>
                                    <textarea className="sc-textarea" rows={3} value={createForm.patient_consent_notes}
                                        onChange={e => updateForm('patient_consent_notes', e.target.value)}
                                        placeholder="Any notes regarding patient consent..." />
                                </div>
                            </>
                        )}

                        {createStep === 5 && (
                            <>
                                <div style={{ background: 'rgba(15, 118, 110, 0.04)', borderRadius: 12, padding: 20, marginBottom: 16 }}>
                                    <h4 style={{ fontSize: '0.88rem', fontWeight: 700, marginBottom: 16 }}>Review Your Request</h4>
                                    <div className="sc-detail-grid">
                                        <div className="sc-detail-item"><label>Title</label><span>{createForm.title || '—'}</span></div>
                                        <div className="sc-detail-item"><label>Type</label><span>{createForm.request_type === 'open' ? 'Public' : 'Direct'}</span></div>
                                        <div className="sc-detail-item"><label>Priority</label><span style={{ textTransform: 'capitalize' }}>{createForm.priority}</span></div>
                                        <div className="sc-detail-item"><label>Engagement</label><span style={{ textTransform: 'capitalize' }}>{createForm.engagement_type}</span></div>
                                        <div className="sc-detail-item"><label>Start Date</label><span>{fmtDate(createForm.start_date)}</span></div>
                                        <div className="sc-detail-item"><label>End Date</label><span>{fmtDate(createForm.end_date)}</span></div>
                                        <div className="sc-detail-item"><label>Budget</label><span>{fmtPay(parseInt(createForm.min_pay) || undefined, parseInt(createForm.max_pay) || undefined)}</span></div>
                                        <div className="sc-detail-item"><label>Response Time</label><span>{createForm.response_time}h</span></div>
                                    </div>
                                </div>
                                {createForm.required_services.length > 0 && (
                                    <div style={{ background: 'rgba(15, 118, 110, 0.04)', borderRadius: 12, padding: 20, marginBottom: 16 }}>
                                        <h4 style={{ fontSize: '0.88rem', fontWeight: 700, marginBottom: 16 }}>Required Services</h4>
                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                            {createForm.required_services.map((serviceId: number) => {
                                                const selectedService = availableServices.find((service: any) => service.id === serviceId);
                                                return <span key={serviceId} className="sc-badge-type open" style={{ padding: '4px 12px' }}>{selectedService?.name || `Service #${serviceId}`}</span>;
                                            })}
                                        </div>
                                    </div>
                                )}
                                <p className="sc-card-desc" style={{ WebkitLineClamp: 'unset' as any }}>{createForm.description}</p>
                            </>
                        )}
                    </div>

                    <div className="sc-modal-footer">
                        {createStep > 1 && <button className="sc-btn sc-btn-ghost" onClick={() => setCreateStep(s => s - 1)}>Back</button>}
                        <div style={{ flex: 1 }}></div>
                        {createStep < 5 ? (
                            <button className="sc-btn sc-btn-primary" onClick={() => setCreateStep(s => s + 1)}>Continue <i className="bx bx-right-arrow-alt"></i></button>
                        ) : (
                            <button className="sc-btn sc-btn-primary" onClick={CreateServiceRequest}><i className="bx bx-check"></i> Create Request</button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    /* ─── Detail Modal ─── */
    const renderDetailModal = () => {
        if (!detailRequest) return null;
        const r = detailRequest;
        return (
            <div className="sc-overlay" onClick={() => setDetailRequest(null)}>
                <div className="sc-modal sc-modal-lg" onClick={e => e.stopPropagation()}>
                    <div className="sc-modal-header">
                        <div>
                            <h2>{r.title}</h2>
                            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                                <StatusBadge status={r.status} />
                                <PriorityBadge priority={r.priority} />
                                <TypeBadge type={r.request_type} />
                            </div>
                        </div>
                        <button className="sc-modal-close" onClick={() => setDetailRequest(null)}><i className="bx bx-x"></i></button>
                    </div>
                    <div className="sc-modal-body">
                        <div className="sc-detail-section">
                            <h4>Description</h4>
                            <p style={{ fontSize: '0.84rem', color: 'var(--uzima-shell-text-soft)', lineHeight: 1.7 }}>{r.description}</p>
                        </div>
                        <div className="sc-detail-section">
                            <h4>Request Details</h4>
                            <div className="sc-detail-grid">
                                <div className="sc-detail-item"><label>Created by</label><span>{r.creator?.name || '—'}</span></div>
                                <div className="sc-detail-item"><label>Engagement</label><span style={{ textTransform: 'capitalize' }}>{r.engagement_type}</span></div>
                                <div className="sc-detail-item"><label>Start Date</label><span>{fmtDate(r.start_date)}</span></div>
                                <div className="sc-detail-item"><label>End Date</label><span>{fmtDate(r.end_date)}</span></div>
                                <div className="sc-detail-item"><label>Response Time</label><span>{r.response_time || '—'}h</span></div>
                                <div className="sc-detail-item"><label>Budget Range</label><span>{fmtPay(r.min_pay, r.max_pay)}</span></div>
                                <div className="sc-detail-item"><label>Created</label><span>{fmtDate(r.created_at)}</span></div>
                                <div className="sc-detail-item"><label>Last Updated</label><span>{fmtDate(r.updated_at)}</span></div>
                            </div>
                        </div>
                        {r.required_services && r.required_services.length > 0 && (
                            <div className="sc-detail-section">
                                <h4>Required Services</h4>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    {r.required_services.map((s, i) => (
                                        <span key={i} className="sc-badge-type open" style={{ padding: '4px 12px' }}>{s}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="sc-modal-footer">
                        <button className="sc-btn sc-btn-ghost" onClick={() => setDetailRequest(null)}>Close</button>
                        {r.creator?.id !== clinicId && (
                            <button className="sc-btn sc-btn-primary" onClick={() => { setDetailRequest(null); resetApplyForm(); setShowApply(r); }}>
                                <i className="bx bx-send"></i> Apply to This Request
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    /* ─── Apply Modal ─── */
    const renderApplyModal = () => {
        if (!showApply) return null;
        const applySteps = ['Rates', 'Qualifications', 'Documents', 'Review'];
        const updateApply = (field: keyof ApplyFormState, value: string) => setApplyForm(prev => ({ ...prev, [field]: value }));

        return (
            <div className="sc-overlay" onClick={() => { setShowApply(null); resetApplyForm(); }}>
                <div className="sc-modal" onClick={e => e.stopPropagation()}>
                    <div className="sc-modal-header">
                        <div>
                            <h2>Apply to Request</h2>
                            <p style={{ fontSize: '0.78rem', color: 'var(--uzima-shell-text-soft)', margin: '4px 0 0' }}>{showApply.title}</p>
                        </div>
                        <button className="sc-modal-close" onClick={() => { setShowApply(null); resetApplyForm(); }}><i className="bx bx-x"></i></button>
                    </div>
                    <div className="sc-steps" style={{ paddingTop: 16 }}>
                        {applySteps.map((stepLabel, index) => (
                            <div key={stepLabel} className={`sc-step ${applyStep === index + 1 ? 'active' : applyStep > index + 1 ? 'done' : ''}`}>
                                <div className="sc-step-num">{applyStep > index + 1 ? <i className="bx bx-check"></i> : index + 1}</div>
                                <span className="sc-step-label">{stepLabel}</span>
                            </div>
                        ))}
                    </div>
                    <div className="sc-modal-body">
                        {applyStep === 1 ? (
                        <>
                        <div className="sc-form-row">
                            <div className="sc-form-group">
                                <label>Your Minimum Rate (KES) <span>*</span></label>
                                <input className="sc-input" type="number" value={applyForm.partner_minimum} onChange={e => updateApply('partner_minimum', e.target.value)} placeholder="e.g. 5000" />
                            </div>
                            <div className="sc-form-group">
                                <label>Your Maximum Rate (KES)</label>
                                <input className="sc-input" type="number" value={applyForm.partner_maximum} onChange={e => updateApply('partner_maximum', e.target.value)} placeholder="e.g. 15000" />
                            </div>
                        </div>
                        <div className="sc-form-row">
                            <div className="sc-form-group">
                                <label>Payment Mode</label>
                                <select className="sc-input" value={applyForm.pay_mode} onChange={e => updateApply('pay_mode', e.target.value)}>
                                    <option value="milestone">Per Milestone</option>
                                    <option value="person">Per Person</option>
                                    <option value="request">Per Request</option>
                                </select>
                            </div>
                            <div className="sc-form-group">
                                <label>Experience (years)</label>
                                <input className="sc-input" type="number" value={applyForm.partner_experience_years} onChange={e => updateApply('partner_experience_years', e.target.value)} placeholder="e.g. 5" />
                            </div>
                        </div>
                        <div className="sc-form-row">
                            <div className="sc-form-group">
                                <label>Patient Capacity</label>
                                <input className="sc-input" type="number" value={applyForm.patient_flow} onChange={e => updateApply('patient_flow', e.target.value)} />
                            </div>
                            <div className="sc-form-group">
                                <label>Capacity Rate</label>
                                <select className="sc-input" value={applyForm.patient_flow_rate} onChange={e => updateApply('patient_flow_rate', e.target.value)}>
                                    <option value="hourly">Per Hour</option>
                                    <option value="daily">Per Day</option>
                                    <option value="weekly">Per Week</option>
                                    <option value="monthly">Per Month</option>
                                </select>
                            </div>
                        </div>
                        </>
                        ) : null}

                        {applyStep === 2 ? (
                        <>
                        <div className="sc-form-group">
                            <label>Qualifications & Experience <span>*</span></label>
                            <textarea className="sc-textarea" rows={3} value={applyForm.partner_qualifications} onChange={e => updateApply('partner_qualifications', e.target.value)} placeholder="Describe your relevant qualifications and experience..." />
                        </div>
                        <div className="sc-form-group">
                            <label>Certifications</label>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <input className="sc-input" value={applyCertificationDraft} onChange={e => setApplyCertificationDraft(e.target.value)} placeholder="Add one certification and press Add" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addApplyArrayItem('partner_certifications', applyCertificationDraft, () => setApplyCertificationDraft('')); } }} />
                                <button className="sc-btn sc-btn-outline" onClick={() => addApplyArrayItem('partner_certifications', applyCertificationDraft, () => setApplyCertificationDraft(''))}>Add</button>
                            </div>
                            {applyForm.partner_certifications.length > 0 ? (
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                                    {applyForm.partner_certifications.map(item => (
                                        <span key={item} className="sc-chip">
                                            {item}
                                            <i className="bx bx-x" style={{ cursor: 'pointer' }} onClick={() => removeApplyArrayItem('partner_certifications', item)}></i>
                                        </span>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                        <div className="sc-form-group">
                            <label>Equipment & Facilities</label>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <input className="sc-input" value={applyEquipmentDraft} onChange={e => setApplyEquipmentDraft(e.target.value)} placeholder="Add one equipment/facility and press Add" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addApplyArrayItem('partner_equipment', applyEquipmentDraft, () => setApplyEquipmentDraft('')); } }} />
                                <button className="sc-btn sc-btn-outline" onClick={() => addApplyArrayItem('partner_equipment', applyEquipmentDraft, () => setApplyEquipmentDraft(''))}>Add</button>
                            </div>
                            {applyForm.partner_equipment.length > 0 ? (
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                                    {applyForm.partner_equipment.map(item => (
                                        <span key={item} className="sc-chip soft">
                                            {item}
                                            <i className="bx bx-x" style={{ cursor: 'pointer' }} onClick={() => removeApplyArrayItem('partner_equipment', item)}></i>
                                        </span>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                        <div className="sc-form-group">
                            <label>Additional Notes</label>
                            <textarea className="sc-textarea" rows={2} value={applyForm.application_notes} onChange={e => updateApply('application_notes', e.target.value)} placeholder="Any additional information..." />
                        </div>
                        </>
                        ) : null}

                        {applyStep === 3 ? (
                        <div className="sc-form-group" style={{ marginTop: 12 }}>
                            <label>Documents & Media</label>
                            <p style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-soft)', margin: '2px 0 8px' }}>
                                Attach certification documents, equipment photos, licenses, etc.
                            </p>
                            {applyAttachments.length === 0 ? (
                                <div className="sc-upload-empty-state">
                                    <div>
                                        <strong>Add supporting files</strong>
                                        <p>Upload certifications, equipment images, licenses, and any supporting application documents.</p>
                                    </div>
                                    <button className="sc-btn sc-btn-primary" onClick={() => setApplyAttachments([{ label: '', file: null }])}>
                                        <i className="bx bx-upload"></i> Add First File
                                    </button>
                                </div>
                            ) : null}
                            {applyAttachments.map((att, idx) => (
                                <div key={idx} className="sc-upload-card">
                                    <div className="sc-upload-card-head">
                                        <strong>Attachment {idx + 1}</strong>
                                        <button className="sc-btn sc-btn-ghost sc-btn-sm" style={{ color: '#e11d48', flexShrink: 0 }}
                                        onClick={() => setApplyAttachments(applyAttachments.filter((_, i) => i !== idx))}>
                                        <i className="bx bx-trash"></i>
                                    </button>
                                    </div>
                                    <input className="sc-input" placeholder="Label (e.g. Board Certification)" value={att.label}
                                        onChange={e => {
                                            const updated = [...applyAttachments];
                                            updated[idx] = { ...updated[idx], label: e.target.value };
                                            setApplyAttachments(updated);
                                        }} />
                                    <div className="sc-upload-picker-row">
                                        <input id={`sc-apply-file-${idx}`} type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv" className="sc-upload-input"
                                            onChange={e => {
                                                const updated = [...applyAttachments];
                                                updated[idx] = { ...updated[idx], file: e.target.files?.[0] || null };
                                                setApplyAttachments(updated);
                                            }} />
                                        <label htmlFor={`sc-apply-file-${idx}`} className="sc-upload-trigger">
                                            <i className="bx bx-folder-open"></i>
                                            {att.file ? 'Choose another file' : 'Choose file'}
                                        </label>
                                        <div className="sc-upload-meta">
                                            <strong>{att.file ? att.file.name : 'No file selected yet'}</strong>
                                            <span>{att.file ? `${Math.max(1, Math.round(att.file.size / 1024))} KB` : 'Accepted: images, PDF, Word, Excel, CSV'}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <button className="sc-btn sc-btn-outline sc-btn-sm" onClick={() => setApplyAttachments([...applyAttachments, { label: '', file: null }])}>
                                <i className="bx bx-plus"></i> Add Another File
                            </button>
                        </div>
                        ) : null}

                        {applyStep === 4 ? (
                        <div style={{ background: 'rgba(15, 118, 110, 0.04)', borderRadius: 12, padding: 20 }}>
                            <h4 style={{ fontSize: '0.88rem', fontWeight: 700, marginBottom: 16 }}>Review Application</h4>
                            <div className="sc-detail-grid">
                                <div className="sc-detail-item"><label>Request</label><span>{showApply.title}</span></div>
                                <div className="sc-detail-item"><label>Rate</label><span>{fmtPay(parseInt(applyForm.partner_minimum) || undefined, parseInt(applyForm.partner_maximum) || undefined)}</span></div>
                                <div className="sc-detail-item"><label>Payment Mode</label><span>{applyForm.pay_mode}</span></div>
                                <div className="sc-detail-item"><label>Capacity</label><span>{applyForm.patient_flow} / {applyForm.patient_flow_rate}</span></div>
                            </div>
                            <div style={{ marginTop: 14 }}>
                                <p className="sc-card-desc" style={{ WebkitLineClamp: 'unset' as any }}>{applyForm.partner_qualifications || 'No qualifications entered yet.'}</p>
                            </div>
                            {applyForm.partner_certifications.length > 0 ? (
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
                                    {applyForm.partner_certifications.map(item => <span key={item} className="sc-chip">{item}</span>)}
                                </div>
                            ) : null}
                            {applyForm.partner_equipment.length > 0 ? (
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                                    {applyForm.partner_equipment.map(item => <span key={item} className="sc-chip soft">{item}</span>)}
                                </div>
                            ) : null}
                            {applyAttachments.length > 0 ? (
                                <div style={{ marginTop: 14 }}>
                                    <p style={{ marginBottom: 8, fontSize: '0.78rem', color: 'var(--uzima-shell-text-soft)' }}>{applyAttachments.length} document{applyAttachments.length === 1 ? '' : 's'} prepared for upload</p>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {applyAttachments.filter(item => item.file).map((item, index) => (
                                            <div key={`${item.label}-${index}`} className="sc-list-item">
                                                <div>
                                                    <strong>{item.label || item.file?.name || 'Attachment'}</strong>
                                                    <p style={{ margin: '4px 0 0', fontSize: '0.74rem', color: 'var(--uzima-shell-text-soft)' }}>{item.file?.name}</p>
                                                </div>
                                                <span className="sc-chip soft">{item.file ? `${Math.max(1, Math.round(item.file.size / 1024))} KB` : 'Pending'}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                        ) : null}
                    </div>
                    <div className="sc-modal-footer">
                        <button className="sc-btn sc-btn-ghost" onClick={() => { setShowApply(null); resetApplyForm(); }}>Cancel</button>
                        {applyStep > 1 ? <button className="sc-btn sc-btn-outline" onClick={() => setApplyStep(step => step - 1)}>Back</button> : null}
                        {applyStep < 4 ? <button className="sc-btn sc-btn-primary" onClick={() => setApplyStep(step => step + 1)}>Continue <i className="bx bx-right-arrow-alt"></i></button> : null}
                        {applyStep === 4 ? <button className="sc-btn sc-btn-primary" onClick={submitApplication} disabled={submitting}>{submitting ? <><i className="bx bx-loader-alt bx-spin"></i> Submitting...</> : <><i className="bx bx-send"></i> Submit Application</>}</button> : null}
                    </div>
                </div>
            </div>
        );
    };

    /* ─── Render Application Detail Page ─── */
    const renderApplicationDetailPage = () => {
        if (viewingAppLoading) {
            return (
                <div className="sc-wrap">
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 80 }}>
                        <i className="bx bx-loader-alt bx-spin" style={{ fontSize: '2.2rem', color: '#0f766e' }}></i>
                    </div>
                </div>
            );
        }
        if (!viewingApplication) return null;

        const sr = viewingApplication.service_request;
        const app = viewingApplication.application;
        const negs = viewingApplication.negotiations || [];
        const docs = viewingApplication.documents || [];
        const isEditable = !['accepted', 'rejected', 'withdrawn', 'patient_approved', 'patient_rejected'].includes(app.status);
        const isOwner = app.applicant.id === clinicId;
        const isRequestCreator = sr.creator?.id === clinicId;

        return (
            <div className="sc-wrap">
                {/* Page header with back button */}
                <div className="sc-header" style={{ alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <button className="sc-btn sc-btn-outline sc-btn-sm" onClick={() => { setViewingApplication(null); setAmendMode(false); setTab('applications'); }} title="Back to Applications">
                            <i className="bx bx-arrow-left" style={{ fontSize: '1.1rem' }}></i>
                            Back to Applications
                        </button>
                        <div>
                            <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                Application #{app.id}
                                <span className={`sc-badge-status ${app.status}`}>{app.status.replace('_', ' ')}</span>
                            </h1>
                            <p>Submitted {fmtDate(app.created)} by {app.applicant?.name || '—'}</p>
                        </div>
                    </div>
                    {/* Action Buttons */}
                    <div className="sc-app-actions">
                        {/* Request creator actions: Accept / Reject */}
                        {isRequestCreator && isEditable && (
                            <>
                                <button className="sc-btn sc-btn-success sc-btn-sm" onClick={() => updateApplicationStatus(app.id, 'accepted', 'Accept Application?', `Accept ${app.applicant?.name}'s application? This will move to patient approval.`)}>
                                    <i className="bx bx-check-circle"></i> Accept
                                </button>
                                <button className="sc-btn sc-btn-danger sc-btn-sm" onClick={() => updateApplicationStatus(app.id, 'rejected', 'Reject Application?', `Reject this application from ${app.applicant?.name}?`)}>
                                    <i className="bx bx-x-circle"></i> Reject
                                </button>
                            </>
                        )}
                        {/* Applicant actions: Withdraw */}
                        {isOwner && isEditable && (
                            <button className="sc-btn sc-btn-danger-outline sc-btn-sm" onClick={() => withdrawApplication(app.id)}>
                                <i className="bx bx-undo"></i> Withdraw
                            </button>
                        )}
                        {/* Accepted / Agreed: Upgrade to Collaboration */}
                        {(app.status === 'accepted' || app.status === 'patient_approved' || sr.status === 'agreed') && isRequestCreator && (
                            <button className="sc-btn sc-btn-primary sc-btn-sm" onClick={() => upgradeToCollaboration(sr.id)}>
                                <i className="bx bx-transfer-alt"></i> Create Collaboration
                            </button>
                        )}
                    </div>
                </div>

                {/* Two-column layout for larger screens */}
                <div className="sc-app-detail-layout">
                    {/* Left column: Service Request + Application */}
                    <div className="sc-app-detail-main">
                        {/* Service Request Section */}
                        <div className="sc-detail-section">
                            <h3 className="sc-detail-section-title"><i className="bx bx-file"></i> Original Service Request</h3>
                            <div className="sc-detail-card">
                                <h4>{sr.title || 'Service Request'}</h4>
                                <p style={{ margin: '8px 0', color: 'var(--uzima-shell-text-soft)', fontSize: '0.82rem', lineHeight: 1.5 }}>{sr.description}</p>
                                <div className="sc-detail-grid">
                                    <div className="sc-detail-item"><label>Status</label><span className={`sc-badge-status ${sr.status}`}>{sr.status}</span></div>
                                    <div className="sc-detail-item"><label>Priority</label><span className={`sc-badge-priority ${sr.priority}`}>{sr.priority}</span></div>
                                    <div className="sc-detail-item"><label>Type</label><span>{sr.engagement_type}</span></div>
                                    <div className="sc-detail-item"><label>Scope</label><span>{sr.request_type}</span></div>
                                    <div className="sc-detail-item"><label>Pay Range</label><span>{fmtPay(sr.min_pay, sr.max_pay)}</span></div>
                                    <div className="sc-detail-item"><label>Response Time</label><span>{sr.response_time || '-'}</span></div>
                                    <div className="sc-detail-item"><label>Start</label><span>{sr.start_date ? fmtDate(sr.start_date) : '-'}</span></div>
                                    <div className="sc-detail-item"><label>End</label><span>{sr.end_date ? fmtDate(sr.end_date) : '-'}</span></div>
                                    <div className="sc-detail-item"><label>Posted by</label><span>{sr.creator?.name || '-'}</span></div>
                                    {sr.patient_name && <div className="sc-detail-item"><label>Patient</label><span>{sr.patient_name}</span></div>}
                                </div>
                            </div>
                        </div>

                        {/* Application Section */}
                        <div className="sc-detail-section">
                            <h3 className="sc-detail-section-title"><i className="bx bx-envelope"></i> Application Details</h3>
                            {!amendMode ? (
                            <div className="sc-detail-card">
                                <div className="sc-detail-grid">
                                    <div className="sc-detail-item"><label>Proposed Rate</label><span>{fmtPay(app.partner_minimum, app.partner_maximum)}</span></div>
                                    <div className="sc-detail-item"><label>Pay Mode</label><span>{app.pay_mode || '-'}</span></div>
                                    <div className="sc-detail-item"><label>Patient Capacity</label><span>{app.patient_flow || '-'} / {app.patient_flow_rate || '-'}</span></div>
                                    <div className="sc-detail-item"><label>Experience</label><span>{app.partner_experience_years ? `${app.partner_experience_years} years` : '-'}</span></div>
                                    <div className="sc-detail-item"><label>Applicant</label><span>{app.applicant?.name}</span></div>
                                    <div className="sc-detail-item"><label>Submitted</label><span>{fmtDate(app.created)}</span></div>
                                </div>
                                <div style={{ marginTop: 16 }}>
                                    <label style={{ fontWeight: 600, fontSize: '0.78rem', color: 'var(--uzima-shell-text-soft)', marginBottom: 4, display: 'block' }}>Qualifications</label>
                                    <p style={{ fontSize: '0.82rem', lineHeight: 1.5 }}>{app.partner_qualifications || '-'}</p>
                                </div>
                                {app.application_notes && (
                                    <div style={{ marginTop: 12 }}>
                                        <label style={{ fontWeight: 600, fontSize: '0.78rem', color: 'var(--uzima-shell-text-soft)', marginBottom: 4, display: 'block' }}>Notes</label>
                                        <p style={{ fontSize: '0.82rem', lineHeight: 1.5 }}>{app.application_notes}</p>
                                    </div>
                                )}
                                {app.partner_certifications?.length > 0 && (
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
                                        {app.partner_certifications.map((c: string) => <span key={c} className="sc-chip">{c}</span>)}
                                    </div>
                                )}
                                {app.partner_equipment?.length > 0 && (
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                                        {app.partner_equipment.map((e: string) => <span key={e} className="sc-chip soft">{e}</span>)}
                                    </div>
                                )}
                                {isOwner && isEditable && (
                                    <div style={{ marginTop: 18 }}>
                                        <button className="sc-btn sc-btn-outline sc-btn-sm" onClick={() => {
                                            setAmendForm({
                                                partner_minimum: String(app.partner_minimum || ''),
                                                partner_maximum: String(app.partner_maximum || ''),
                                                pay_mode: app.pay_mode || 'per_session',
                                                patient_flow: String(app.patient_flow || ''),
                                                patient_flow_rate: app.patient_flow_rate || 'daily',
                                                partner_qualifications: app.partner_qualifications || '',
                                                application_notes: app.application_notes || ''
                                            });
                                            setAmendMode(true);
                                        }}><i className="bx bx-edit-alt"></i> Amend Application</button>
                                    </div>
                                )}
                            </div>
                            ) : (
                            <div className="sc-detail-card" style={{ border: '2px solid #0f766e' }}>
                                <h4 style={{ fontSize: '0.88rem', marginBottom: 14, color: '#0f766e' }}><i className="bx bx-edit-alt"></i> Amend Your Application</h4>
                                <div className="sc-form-row">
                                    <div className="sc-form-group"><label>Min Rate</label><input className="sc-input" type="number" value={amendForm.partner_minimum} onChange={e => setAmendForm(f => ({ ...f, partner_minimum: e.target.value }))} /></div>
                                    <div className="sc-form-group"><label>Max Rate</label><input className="sc-input" type="number" value={amendForm.partner_maximum} onChange={e => setAmendForm(f => ({ ...f, partner_maximum: e.target.value }))} /></div>
                                </div>
                                <div className="sc-form-row">
                                    <div className="sc-form-group"><label>Pay Mode</label>
                                        <select className="sc-select" value={amendForm.pay_mode} onChange={e => setAmendForm(f => ({ ...f, pay_mode: e.target.value }))}>
                                            <option value="per_session">Per Session</option><option value="hourly">Hourly</option><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option><option value="per_patient">Per Patient</option><option value="flat_rate">Flat Rate</option>
                                        </select>
                                    </div>
                                    <div className="sc-form-group"><label>Capacity</label><input className="sc-input" type="number" value={amendForm.patient_flow} onChange={e => setAmendForm(f => ({ ...f, patient_flow: e.target.value }))} /></div>
                                </div>
                                <div className="sc-form-group"><label>Qualifications</label><textarea className="sc-textarea" rows={3} value={amendForm.partner_qualifications} onChange={e => setAmendForm(f => ({ ...f, partner_qualifications: e.target.value }))} /></div>
                                <div className="sc-form-group"><label>Notes</label><textarea className="sc-textarea" rows={2} value={amendForm.application_notes} onChange={e => setAmendForm(f => ({ ...f, application_notes: e.target.value }))} /></div>
                                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                                    <button className="sc-btn sc-btn-primary sc-btn-sm" onClick={submitAmendment} disabled={amendSubmitting}>{amendSubmitting ? <><i className="bx bx-loader-alt bx-spin"></i> Saving...</> : <><i className="bx bx-check"></i> Save Changes</>}</button>
                                    <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => setAmendMode(false)}>Cancel</button>
                                </div>
                            </div>
                            )}
                        </div>

                        {/* Documents Section */}
                        {docs.length > 0 && (
                        <div className="sc-detail-section">
                            <h3 className="sc-detail-section-title"><i className="bx bx-folder-open"></i> Documents ({docs.length})</h3>
                            <div className="sc-detail-docs-grid">
                                {docs.map((doc: any) => (
                                    <a key={doc.id} href={doc.file} target="_blank" rel="noopener noreferrer" className="sc-doc-card">
                                        <i className={`bx ${doc.file_name?.match(/\.(pdf)$/i) ? 'bx-file-blank' : doc.file_name?.match(/\.(jpe?g|png|gif|webp|svg)$/i) ? 'bx-image' : 'bx-file'}`}></i>
                                        <div>
                                            <strong>{doc.label || doc.file_name}</strong>
                                            <span>{doc.category} / {doc.subcategory}</span>
                                        </div>
                                    </a>
                                ))}
                            </div>
                        </div>
                        )}
                    </div>

                    {/* Right column: Negotiations */}
                    <div className="sc-app-detail-sidebar">
                        <div className="sc-detail-section">
                            <h3 className="sc-detail-section-title"><i className="bx bx-chat"></i> Negotiation History ({negs.length})</h3>
                            {negs.length === 0 ? (
                                <p style={{ color: 'var(--uzima-shell-text-soft)', fontSize: '0.82rem' }}>No messages yet.</p>
                            ) : (
                            <div className="sc-negotiation-thread">
                                {negs.map((n: any) => (
                                    <div key={n.id} className={`sc-neg-msg ${n.sender.id === clinicId ? 'mine' : 'theirs'}`}>
                                        <div className="sc-neg-msg-head">
                                            <strong>{n.sender.name}</strong>
                                            <span>{fmtDate(n.created)}</span>
                                        </div>
                                        <p>{n.message}</p>
                                        {(n.proposed_rate || n.proposed_pay_mode) && (
                                            <div className="sc-neg-proposal">
                                                {n.proposed_rate && <span><i className="bx bx-money"></i> KES {Number(n.proposed_rate).toLocaleString()}</span>}
                                                {n.proposed_pay_mode && <span><i className="bx bx-time-five"></i> {n.proposed_pay_mode}</span>}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            )}
                            {/* Message composer */}
                            {isEditable && (
                            <div className="sc-neg-composer">
                                <textarea className="sc-textarea" rows={2} value={negotiationMsg} onChange={e => setNegotiationMsg(e.target.value)} placeholder="Type a message or counter-offer..." />
                                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                                    <button className="sc-btn sc-btn-primary sc-btn-sm" onClick={() => sendNegotiationMessage('message')} disabled={!negotiationMsg.trim()}>
                                        <i className="bx bx-send"></i> Send
                                    </button>
                                    <button className="sc-btn sc-btn-outline sc-btn-sm" onClick={() => sendNegotiationMessage('counter_offer')} disabled={!negotiationMsg.trim()}>
                                        <i className="bx bx-transfer"></i> Counter Offer
                                    </button>
                                </div>
                            </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    /* ─── Main Render ─── */
    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    {(viewingApplication || viewingAppLoading) ? renderApplicationDetailPage() : (
                    <div className="sc-wrap">
                        {/* Header */}
                        <div className="sc-header">
                            <div>
                                <h1>Service Request Center</h1>
                                <p>Manage service requests, browse opportunities, and track applications</p>
                            </div>
                            <button className="sc-btn sc-btn-primary" onClick={() => setShowCreate(true)}>
                                <i className="bx bx-plus"></i> New Request
                            </button>
                        </div>

                        {/* Tabs */}
                        <div className="sc-tabs">
                            <button className={`sc-tab ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>
                                <i className="bx bx-grid-alt"></i> Overview
                            </button>
                            <button className={`sc-tab ${tab === 'my-requests' ? 'active' : ''}`} onClick={() => setTab('my-requests')}>
                                <i className="bx bx-file"></i> My Requests
                                {stats.myRequests > 0 && <span className="sc-badge">{stats.myRequests}</span>}
                            </button>
                            <button className={`sc-tab ${tab === 'browse' ? 'active' : ''}`} onClick={() => setTab('browse')}>
                                <i className="bx bx-globe"></i> Browse & Apply
                                {stats.openRequests > 0 && <span className="sc-badge">{stats.openRequests}</span>}
                            </button>
                            <button className={`sc-tab ${tab === 'direct' ? 'active' : ''}`} onClick={() => setTab('direct')}>
                                <i className="bx bx-mail-send"></i> Direct Requests
                                {stats.directRequests > 0 && <span className="sc-badge">{stats.directRequests}</span>}
                            </button>
                            <button className={`sc-tab ${tab === 'applications' ? 'active' : ''}`} onClick={() => setTab('applications')}>
                                <i className="bx bx-envelope"></i> Applications
                                {(stats.receivedApps + stats.sentApps) > 0 && <span className="sc-badge">{stats.receivedApps + stats.sentApps}</span>}
                            </button>
                        </div>

                        {/* Tab Content */}
                        {loading ? (
                            <div className="sc-spinner"></div>
                        ) : (
                            <>
                                {tab === 'overview' && renderOverview()}
                                {tab === 'my-requests' && renderMyRequests()}
                                {tab === 'browse' && renderBrowse()}
                                {tab === 'direct' && renderDirect()}
                                {tab === 'applications' && renderApplications()}
                            </>
                        )}
                    </div>
                    )}
                </div>
                <DashboardFooter />
            </div>
            <Canvas />

            {/* Modals */}
            {showCreate && renderCreateModal()}
            {detailRequest && renderDetailModal()}
            {showApply && renderApplyModal()}
        </>
    );
};

export default ServiceCenter;
